import React, { useEffect, useRef, useState } from 'react';
import Typography from '@mui/material/Typography';
import { SxStyle } from '../../../../../types';
import Button from '@mui/material/Button';
import {
  getContractUiSchema,
  getCurrentClubContentUiSchema,
  getHealthStatusUiSchema,
  getPlayerContentUiSchema,
} from '../../UI-schema';
import SquadDialog, { SquadDialogProps } from '../squad-dialog';
import { useStringsContext } from '../../../../../contexts/strings';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import LoadingButton from '@mui/lab/LoadingButton';
import { SquadPlayer } from '../../types';
import { Field, FIELD_TYPES, UIschema } from '../../../../../types';
import RenderModuleFields from '../../../../common/fields/render-fields';
import Grid from '@mui/material/Grid';
import { applyNewFieldValue, isAllFieldsValid } from '../../../../../utils/team';
import FormControl from '@mui/material/FormControl';
import { PLAYER_TABS } from '../../player-container';
import { usePlayer } from '../../hooks/player';

const styles: SxStyle = {
  title: {
    marginBottom: '2px',
  },
  content: {
    paddingTop: '10px',
  },
  buttons: (theme) => ({
    borderTop: '1px solid',
    borderTopColor: theme.palette.divider,
  }),
};

interface InfoFieldProps {
  field: Field;
  onChange: (field: Field, value: string | number | boolean) => void;
  onValidation: (field: Field, value: string | number | boolean) => void;
}

const InfoField = ({ field, onChange, onValidation }: InfoFieldProps) => {
  return (
    <>
      {field.type === FIELD_TYPES.COLOR_PICKER && (
        <Typography sx={styles.title} variant="subtitle2">
          {field.title}
        </Typography>
      )}
      <RenderModuleFields
        field={field}
        setChange={(key, value) => {
          onChange(field, value);
        }}
        setValidation={(key, value) => {
          onValidation(field, value);
        }}
        path={'match.params.id'}
        fullWidth={true}
      />
    </>
  );
};

interface Props extends Pick<SquadDialogProps, 'open' | 'onClose'> {
  selectedTab: PLAYER_TABS;
  player: SquadPlayer;
  teamId: string;
  onUpdatePlayer: (updatedPlayer: SquadPlayer) => void;
}

export default function EditPlayerModal({
  selectedTab,
  open,
  onClose,
  onUpdatePlayer,
  player,
  teamId,
}: Props) {
  const { strings } = useStringsContext();
  const valuesFields = useRef<Record<string, string | number | boolean>>({});
  const valuesCurrentClubFields = useRef<Record<string, string | number | boolean>>({});
  const editedFields = useRef<Record<string, boolean>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [loading, setLoading] = useState(false);
  const { updatePlayer, updateCurrentClub } = usePlayer();
  const [contentPlayerUIschema, setContentPlayerUIschema] = useState<UIschema | null>(null);
  const [contentCurrentClubUIschema, setContentCurrentClubUIschema] = useState<UIschema | null>(
    null
  );

  useEffect(() => {
    const contentPlayerUIschemas = getPlayerContentUiSchema(player);
    const contentCurrentClubUIschemas = player.activeCurrentClub
      ? getCurrentClubContentUiSchema(player.activeCurrentClub)
      : [];
    const contentHealthStatusUIschemas = getHealthStatusUiSchema(player);
    const contentContractUIschemas = player.activeCurrentClub
      ? getContractUiSchema(player.activeCurrentClub)
      : [];
    let contentPlayerUIschema = null;
    let contentCurrentClubUIschema = null;

    if (selectedTab === PLAYER_TABS.INFORMATION) {
      contentPlayerUIschema = contentPlayerUIschemas[0]; // Info
      contentCurrentClubUIschema = contentCurrentClubUIschemas[0];
    } else if (selectedTab === PLAYER_TABS.SOCIAL) {
      contentPlayerUIschema = contentPlayerUIschemas[1]; // Social
    } else if (selectedTab === PLAYER_TABS.DATA) {
      contentPlayerUIschema = contentPlayerUIschemas[2]; // Data
    } else if (selectedTab === PLAYER_TABS.HEALTH) {
      contentPlayerUIschema = contentHealthStatusUIschemas[0]; // Health
    } else if (selectedTab === PLAYER_TABS.CONTRACT) {
      contentCurrentClubUIschema = contentContractUIschemas[0]; // Contract
    }

    setContentPlayerUIschema(contentPlayerUIschema);
    setContentCurrentClubUIschema(contentCurrentClubUIschema);
  }, [player, selectedTab]);

  useEffect(() => {
    if (open) {
      setIsDirty(false);
      setLoading(false);
    }
  }, [open]);

  const onSave = async () => {
    setLoading(true);

    const updatedPlayer = { ...player };
    Object.keys(valuesFields.current).forEach(function (key) {
      applyNewFieldValue(updatedPlayer, key, valuesFields.current[key]);
    });

    const currentClub = player.activeCurrentClub;
    const updatedCurrentClub = { ...currentClub };
    Object.keys(valuesCurrentClubFields.current).forEach(function (key) {
      applyNewFieldValue(updatedCurrentClub, key, valuesCurrentClubFields.current[key]);
    });

    try {
      await updatePlayer(updatedPlayer);
      const hasCurrentClubEdits = Object.keys(valuesCurrentClubFields.current).length > 0;
      if (hasCurrentClubEdits || currentClub?.id) {
        await updateCurrentClub(updatedPlayer, updatedCurrentClub, teamId);
      }
      onUpdatePlayer(updatedPlayer);
      if (onClose) {
        onClose();
      }
    } finally {
      setLoading(false);
    }
  };

  const onChange = (field: any, value: string | number | boolean) => {
    if (field.key === 'Not available') {
      console.error(`The key: ${field.key} is not defined in the UI-schema.`);
      return;
    }

    valuesFields.current[field.key] = value;

    setIsDirty(true);
  };

  const onChangeCurrentClub = (field: any, value: string | number | boolean) => {
    if (field.key === 'Not available') {
      console.error(`The key: ${field.key} is not defined in the UI-schema.`);
      return;
    }

    valuesCurrentClubFields.current[field.key] = value;

    setIsDirty(true);
  };

  const onValidation = async (field: any, value: string | number | boolean) => {
    const isCurrentFieldValid: boolean = await field.validation.isValid(value);
    editedFields.current[field.key] = isCurrentFieldValid;
    const isAllValid = isAllFieldsValid(editedFields.current);
    setIsValid(isAllValid);
  };

  return (
    <SquadDialog open={open} onClose={onClose} title={strings.squad.editInfo}>
      <DialogContent>
        <FormControl fullWidth sx={styles.content}>
          <Grid item container spacing={3} direction="column">
            {contentCurrentClubUIschema?.fields.map((field) => (
              <Grid item xs key={field.id}>
                <InfoField
                  field={field as Field}
                  onChange={onChangeCurrentClub}
                  onValidation={onValidation}
                />
              </Grid>
            ))}
            {contentPlayerUIschema?.fields.map((field) => (
              <Grid item xs key={field.id}>
                <InfoField field={field as Field} onChange={onChange} onValidation={onValidation} />
              </Grid>
            ))}
          </Grid>
        </FormControl>
      </DialogContent>
      <DialogActions sx={styles.buttons}>
        <Button color="primary" onClick={onClose}>
          {strings.squad.cancel}
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          disabled={!(isDirty && isValid)}
          loading={loading}
          onClick={onSave}
        >
          {strings.squad.save}
        </LoadingButton>
      </DialogActions>
    </SquadDialog>
  );
}
